import translate from '@translate';
import React, { FC, useEffect, useState } from 'react';
import Grid, { GridItem } from 'src/components/Grid';
import Select, { OnChangeType as SelectOnChangeType, OptionType } from 'src/components/Select';
import TextField from 'src/components/TextField';
import { OrderType } from 'src/types/order';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import DateTimePicker from 'src/components/DateTimePicker';
import { Typography } from '@mui/material';
import Switch from 'src/components/Switch';
import { styled } from '@mui/system';
import { OnChangeMultipleType } from './index';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { EditKeysType } from './constants';
import moment from 'moment';

export const StyledSwitchLine = styled('div')({
    margin: '20px 0px'
});

const editableOptions = [
    {
        label: 'transferPaymentPaid',
        value: '2:1'
    },
    {
        label: 'transferPaymentNotPaid',
        value: '2:0'
    },
    {
        label: 'cashOnDeliveryPaid',
        value: '0:1'
    },
    {
        label: 'cashOnDeliveryNotPaid',
        value: '0:0'
    },
    {
        label: 'onlinePaymentPaid',
        value: '1:1'
    },
    {
        label: 'onlinePaymentNotPaid',
        value: '1:0'
    }
];

const nonEditableOptions = [
    {
        label: 'noPaymentPaid',
        value: 'null:1'
    },
    {
        label: 'noPaymentNotPaid',
        value: 'null:0'
    },
];

// values "PAYMENT_METHOD:PAID"
const paymentOptions: OptionType[] = [...editableOptions, ...nonEditableOptions];
const PAYMENT_SEPARATOR = ':';

export interface PaymentMethodPropsType {
    editData: OrderType;
    onChangeMultiple: OnChangeMultipleType;
    disableEdit: boolean;
    editErrors: FormErrorsType;
}

const PaymentMethod: FC<PaymentMethodPropsType> = (props) => {
    const [translatePaymentOptions, setTranslatePaymentOptions] = useState<OptionType[]>([]);
    const [translateEditableOptions, setTranslateEditablePaymentOptions] = useState<OptionType[]>([]);
    const [value, setValue] = useState<string | undefined>(undefined);

    useEffect(() => {
        setTranslatePaymentOptions(
            paymentOptions.map((item) => ({ ...item, label: translate(`paymentEdit.paidAction.${item.label}`) }))
        );
    }, []);

    useEffect(() => {
        setTranslateEditablePaymentOptions(
            editableOptions.map((item) => ({ ...item, label: translate(`paymentEdit.paidAction.${item.label}`) }))
        );
    }, []);

    useEffect(() => {
        const newValue = `${props.editData?.PAYMENT_METHOD}${PAYMENT_SEPARATOR}${props.editData?.PAID}`;
        if (paymentOptions.find((option) => option.value === newValue)) {
            setValue(newValue);
        } else {
            setValue(undefined);
        }
    }, [props.editData?.PAYMENT_METHOD, props.editData?.PAID]);

    const onChange: SelectOnChangeType = (value, error) => {
        if (value && typeof value === 'string') {
            const arrValues = value.split(PAYMENT_SEPARATOR);
            props.onChangeMultiple({
                PAYMENT_METHOD: arrValues[0],
                PAID: Number(arrValues[1]),
                PAYMENT_METHOD_PAID: undefined
            });
        }
    };

    return (
        <Select
            options={translatePaymentOptions}
            value={value}
            error={props.editErrors.PAYMENT_METHOD_PAID}
            onChange={onChange}
            name='PAYMENT_METHOD_PAID'
            label={translate('orderDetail.paymentMethodPaid')}
            fullWidth
            required
            disabled={props.disableEdit}
            editableOptions={translateEditableOptions}
        />
    );
};

export interface InvoicePropsType {
    onChange: OnChangeType<EditKeysType>;
    editData: OrderType;
    editErrors: FormErrorsType;
    disableEdit: boolean;
    permissionEditAll: boolean;
    permissionManagerEdit: boolean;
    disableEditAllowExpedition: boolean;
    disablePaidActions: boolean;
    currencyCodes: ConstantsCodeType[];
    paymentCodes: ConstantsCodeType[];
    summary?: boolean;
    onChangeMultiple: OnChangeMultipleType;
}

const Invoice: FC<InvoicePropsType> = (props) => {
    return (
        <Grid>
            <GridItem cols={12}>
                <Typography variant='h3' gutterBottom>
                    {translate('orderDetail.invoice')}
                </Typography>
            </GridItem>
            <GridItem cols={6}>
                <DateTimePicker
                    value={props.editData.INVOICE_DATE}
                    error={props.editErrors.INVOICE_DATE}
                    onChange={(value, error) => props.onChange('INVOICE_DATE', value && typeof value !== 'string' ? moment(value).format('YYYY-MM-DD') : null, error)}
                    name={'INVOICE_DATE'}
                    label={translate('orderDetail.invoiceDate')}
                    fullWidth
                    disabled={props.disableEdit || props.summary}
                    type='date'
                />
            </GridItem>
            <GridItem cols={6}>
                <TextField
                    value={props.editData.INVOICE_NUMBER}
                    error={props.editErrors.INVOICE_NUMBER}
                    onChange={(value, error) => props.onChange('INVOICE_NUMBER', value, error)}
                    name='INVOICE_NUMBER'
                    label={translate('orderDetail.invoiceNumber')}
                    fullWidth
                    disabled={props.disableEdit || props.summary}
                />
            </GridItem>
            <GridItem cols={6}>
                <TextField
                    value={props.editData.PRICE}
                    error={props.editErrors.PRICE}
                    onChange={(value, error) => props.onChange('PRICE', value, error)}
                    name='PRICE'
                    label={translate('orderDetail.invoicePrice')}
                    fullWidth
                    required
                    type='numberLocale'
                    min={0}
                    disabled={props.disableEdit || props.summary}
                />
            </GridItem>
            <GridItem cols={4}>
                <TextField
                    value={props.editData.SHIPPING_PRICE}
                    error={props.editErrors.SHIPPING_PRICE}
                    onChange={(value, error) => props.onChange('SHIPPING_PRICE', value, error)}
                    name='SHIPPING_PRICE'
                    label={translate('orderDetail.shippingPrice')}
                    fullWidth
                    required
                    type='numberLocale'
                    min={0}
                    disabled={props.disableEdit || props.summary}
                />
            </GridItem>
            <GridItem cols={2}>
                <Select
                    options={props.currencyCodes}
                    value={props.editData.CURRENCY}
                    error={props.editErrors.CURRENCY}
                    onChange={(value, error) => props.onChange('CURRENCY', value, error)}
                    name='CURRENCY'
                    label={translate('orderDetail.currency')}
                    fullWidth
                    required
                    disabled={props.disableEdit || props.summary}
                />
            </GridItem>
            <GridItem cols={6}>
                <PaymentMethod
                    editData={props.editData}
                    editErrors={props.editErrors}
                    onChangeMultiple={props.onChangeMultiple}
                    disableEdit={props.disableEdit || !!props.summary}
                />
            </GridItem>
            {props.editData.PAYMENT_METHOD === '0' && (
                <>
                    <GridItem cols={4}>
                        <TextField
                            value={props.editData.COD_PRICE}
                            error={props.editErrors.COD_PRICE}
                            onChange={(value, error) => props.onChange('COD_PRICE', value, error)}
                            name='COD_PRICE'
                            label={translate('orderDetail.cashOnDeliveryPrice')}
                            fullWidth
                            required
                            type='numberLocale'
                            min={0}
                            disabled={props.disableEdit || props.summary}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <Select
                            options={props.currencyCodes}
                            value={props.editData.COD_CURRENCY}
                            error={props.editErrors.COD_CURRENCY}
                            onChange={(value, error) => props.onChange('COD_CURRENCY', value, error)}
                            name='COD_CURRENCY'
                            label={translate('orderDetail.cashOnDeliveryCurrency')}
                            fullWidth
                            required
                            disabled={props.disableEdit || props.summary}
                        />
                    </GridItem>
                    <GridItem cols={12}>
                        <TextField
                            value={props.editData.COD_REF_NUMBER}
                            error={props.editErrors.COD_REF_NUMBER}
                            onChange={(value, error) => props.onChange('COD_REF_NUMBER', value, error)}
                            name='COD_REF_NUMBER'
                            label={translate('orderDetail.cashOnDeliveryRefNumber')}
                            fullWidth
                            required
                            disabled={props.disableEdit || props.summary}
                        />
                    </GridItem>
                </>
            )}
            <GridItem cols={6}>
                {props.editData.PAYMENT_METHOD === '0' ? (
                    <Switch
                        value={!!props.editData.ALLOW_EXPEDITION}
                        onChange={(value) => props.onChange('ALLOW_EXPEDITION', value ? 1 : 0, false)}
                        disabled={props.disableEdit || props.disableEditAllowExpedition || props.summary}
                        label={translate('orderDetail.allowExpedition')}
                    />
                ) : (
                    <StyledSwitchLine>
                        <Switch
                            value={!!props.editData.ALLOW_EXPEDITION}
                            onChange={(value) => props.onChange('ALLOW_EXPEDITION', value ? 1 : 0, false)}
                            disabled={props.disableEdit || props.disableEditAllowExpedition || props.summary}
                            label={translate('orderDetail.allowExpedition')}
                        />
                    </StyledSwitchLine>
                )}
            </GridItem>
        </Grid>
    );
};

export default Invoice;
